import type {
  LoginResponse,
  SelectOrgResponse,
  SwitchOrgResponse,
  ProfileResponse,
  Patient,
  PaginatedResponse,
  CreatePatientData,
  PatientStatus,
  Procedure,
  ProcedureTechnique,
  Professional,
  Appointment,
  AgendaBlock,
  AppointmentStatus,
  Anamnesis,
  AnamnesisContent,
  AnamnesisStatus,
  AnamnesisType,
  PerinealAssessment,
  Evolution,
  FinancialRecord,
  FinancialStatus,
  FinancialSummary,
  TreatmentPackage,
  PlanUsage,
  SubscriptionData,
  Plan,
  PlanFeatureStatus,
  OrganizationProfile,
  UpdateOrganizationData,
  Task,
  TaskStatus,
  TaskPriority,
  PatientPortalInfo,
  PatientTreatmentPlanFeatures,
  VoidingDiaryEntry,
  BowelDiaryEntry,
  PhysicalExam,
  PhysicalExamSections,
  PhysicalExamStatus,
} from '@/types/clinic';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000';

const REQUEST_TIMEOUT_MS = 30_000;

let refreshInFlight: Promise<void> | null = null;

async function tryRefreshToken(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          throw new ApiError(401, 'Sessão expirada');
        }
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

/** Refresh deduped; se falhar, derruba a sessão (mesmo contrato do request()). */
async function refreshOrLogout(): Promise<void> {
  try {
    await tryRefreshToken();
  } catch {
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new ApiError(401, 'Sessão expirada');
  }
}

/** fetch que, em 401, renova o token e repete UMA vez (blob/upload fora do request()). */
async function fetchWithRefresh(url: string, init: RequestInit): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status !== 401) return res;
  await refreshOrLogout();
  return fetch(url, init);
}

async function request<T>(path: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1${path}`, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    if (!res.ok) {
      const skipRefreshPaths = [
        '/auth/login',
        '/auth/refresh',
        '/auth/logout',
        '/auth/select-organization',
      ];
      // Rotas públicas (sem sessão) nunca disparam refresh nem logout.
      const isPublicPath = path.startsWith('/public/');
      if (res.status === 401 && !_isRetry && !isPublicPath && !skipRefreshPaths.includes(path)) {
        await refreshOrLogout();
        return request<T>(path, options, true);
      }

      const body = await res.json().catch(() => ({}));
      const message = Array.isArray(body.message)
        ? body.message.join(' ')
        : body.message || 'Erro na requisição';
      throw new ApiError(res.status, message, Array.isArray(body.errors) ? body.errors : undefined);
    }

    if (res.status === 204) {
      return undefined as T;
    }
    return res.json();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(408, 'A requisição excedeu o tempo limite. Tente novamente.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: { field: string; messages: string[] }[],
  ) {
    super(message);
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export const authApi = {
  login: (cpf: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { cpf, password }),

  selectOrganization: (preAuthToken: string, organizationId: string) =>
    api.post<SelectOrgResponse>('/auth/select-organization', { preAuthToken, organizationId }),

  switchOrganization: (organizationId: string) =>
    api.post<SwitchOrgResponse>('/auth/switch-organization', { organizationId }),

  logout: () => api.post<{ ok: true }>('/auth/logout', {}),

  me: () => api.get<ProfileResponse>('/auth/me'),
  updateProfile: (data: { name?: string; email?: string; phone?: string }) =>
    api.patch<{ id: string; cpf: string; name: string; email: string | null; phone: string | null }>('/auth/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post<{ message: string }>('/auth/change-password', data),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, newPassword }),
};

export interface LeadRegisterPayload {
  token: string;
  organizationType: 'CLINIC_PJ' | 'SOLO_PF';
  name: string;
  document?: string;
  email: string;
  phone?: string;
  owner: { name: string; cpf: string; email: string; phone?: string };
}

/** Rotas públicas (sem sessão): o token de convite só trafega nestas duas chamadas. */
export const leadInviteApi = {
  getInvite: (token: string) =>
    api.get<{ email: string; name: string | null }>(
      `/public/lead-invite?token=${encodeURIComponent(token)}`,
    ),

  register: (payload: LeadRegisterPayload) =>
    api.post<{ organization: { id: string; name: string }; provisionalPassword: string | null }>(
      '/public/lead-register',
      payload,
    ),
};

function queryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) =>
      v !== undefined &&
      v !== '' &&
      (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'),
  );
  return new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const patientsApi = {
  list: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    orderBy?: 'name_asc' | 'name_desc';
    hasActivePackage?: boolean;
    hasNoUpcomingAppointment?: boolean;
    status?: PatientStatus;
  }) =>
    api.get<PaginatedResponse<Patient>>(`/patients?${queryString(params)}`),
  getById: (id: string) => api.get<Patient>(`/patients/${id}`),
  create: (data: CreatePatientData) => api.post<Patient>('/patients', data),
  update: (id: string, data: Partial<CreatePatientData> & { status?: PatientStatus }) =>
    api.patch<Patient>(`/patients/${id}`, data),
  remove: (id: string) => api.delete<void>(`/patients/${id}`),
};

export const proceduresApi = {
  list: () => api.get<Procedure[]>('/procedures'),
  getById: (id: string) => api.get<Procedure>(`/procedures/${id}`),
  create: (data: { name: string; durationMinutes: number; price: number }) =>
    api.post<Procedure>('/procedures', data),
  update: (id: string, data: Partial<{ name: string; durationMinutes: number; price: number; active: boolean }>) =>
    api.patch<Procedure>(`/procedures/${id}`, data),
  remove: (id: string) => api.delete<void>(`/procedures/${id}`),
  techniques: {
    list: (procedureId: string, includeInactive = false) =>
      api.get<ProcedureTechnique[]>(
        `/procedures/${procedureId}/techniques${includeInactive ? '?includeInactive=true' : ''}`,
      ),
    create: (procedureId: string, data: { name: string }) =>
      api.post<ProcedureTechnique>(`/procedures/${procedureId}/techniques`, data),
    update: (procedureId: string, techniqueId: string, data: { name?: string; active?: boolean }) =>
      api.patch<ProcedureTechnique>(`/procedures/${procedureId}/techniques/${techniqueId}`, data),
    remove: (procedureId: string, techniqueId: string) =>
      api.delete<void>(`/procedures/${procedureId}/techniques/${techniqueId}`),
  },
};

export interface PersonLookupResult {
  exists: boolean;
  maskedName?: string;
  maskedEmail?: string;
}

export const professionalsApi = {
  list: (params?: { search?: string }) =>
    api.get<Professional[]>(`/professionals?${queryString(params)}`),
  getById: (id: string) => api.get<Professional>(`/professionals/${id}`),
  update: (id: string, data: { role?: string; active?: boolean; specialty?: string; professionalRegistration?: string }) =>
    api.patch<Professional>(`/professionals/${id}`, data),
  lookup: (cpf: string) =>
    api.get<PersonLookupResult>(`/organizations/professionals/lookup?${queryString({ cpf })}`),
  invite: (data: {
    cpf: string;
    role: string;
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
  }) => api.post<unknown>('/organizations/professionals', data),
  removeFromOrg: (_orgId: string, userId: string) =>
    api.delete<void>(`/organizations/users/${userId}`),
};

export const appointmentsApi = {
  certificate: (id: string): Promise<Blob> =>
    documentsApi.generate('APPOINTMENT_CERTIFICATE', { appointmentId: id }),
  attendanceTerm: (id: string): Promise<Blob> =>
    documentsApi.generate('ATTENDANCE_TERM', { appointmentId: id }),
  list: (
    params:
      | { startDate: string; endDate: string; professionalId?: string; patientId?: string }
      | { patientId: string; professionalId?: string },
  ) => api.get<Appointment[]>(`/appointments?${queryString(params)}`),
  getById: (id: string) => api.get<Appointment>(`/appointments/${id}`),
  /** Cria a receita já paga (ou dá baixa na pendente). Idempotente. */
  receive: (id: string, data: { paymentMethod?: string } = {}) =>
    api.post<{ id: string; status: FinancialStatus }>(`/appointments/${id}/receive`, data),
  create: (data: {
    patientId: string;
    professionalId: string;
    procedureId: string;
    startAt: string;
    notes?: string;
    treatmentPackageId?: string;
  }) => api.post<Appointment>('/appointments', data),
  createBulk: (data: {
    recurrenceGroupId: string;
    appointments: Array<{
      patientId: string;
      professionalId: string;
      procedureId: string;
      startAt: string;
      notes?: string;
      treatmentPackageId?: string;
      recurrenceIndex: number;
    }>;
  }) => api.post<Appointment[]>('/appointments/bulk', data),
  update: (id: string, data: Record<string, unknown>) =>
    api.patch<Appointment>(`/appointments/${id}`, data),
  updateRecurrenceForward: (id: string, data: Record<string, unknown>) =>
    api.patch<Appointment[]>(`/appointments/${id}/recurrence-forward`, data),
  updateStatus: (
    id: string,
    status: AppointmentStatus,
    options?: { deductFromPackage?: boolean },
  ) => api.patch<Appointment>(`/appointments/${id}/status`, { status, ...options }),
  remove: (id: string) => api.delete<void>(`/appointments/${id}`),
};

export const agendaBlocksApi = {
  list: (params: { startDate: string; endDate: string; professionalId?: string }) =>
    api.get<AgendaBlock[]>(`/agenda-blocks?${queryString(params)}`),
  create: (data: {
    professionalId: string;
    title: string;
    startAt: string;
    endAt: string;
    notes?: string;
  }) => api.post<AgendaBlock>('/agenda-blocks', data),
  update: (id: string, data: Partial<{
    professionalId: string;
    title: string;
    startAt: string;
    endAt: string;
    notes?: string;
  }>) => api.patch<AgendaBlock>(`/agenda-blocks/${id}`, data),
  remove: (id: string) => api.delete<void>(`/agenda-blocks/${id}`),
};

export const anamnesisApi = {
  list: (patientId: string) => api.get<Anamnesis[]>(`/anamneses?patientId=${patientId}`),
  getById: (id: string) => api.get<Anamnesis>(`/anamneses/${id}`),
  create: (data: {
    patientId: string;
    type: AnamnesisType;
    assessmentDate?: string;
    data?: AnamnesisContent;
  }) => api.post<Anamnesis>('/anamneses', data),
  update: (
    id: string,
    data: { data?: AnamnesisContent; assessmentDate?: string; status?: AnamnesisStatus },
  ) => api.patch<Anamnesis>(`/anamneses/${id}`, data),
  remove: (id: string) => api.delete<void>(`/anamneses/${id}`),
  report: (id: string): Promise<Blob> =>
    documentsApi.generate('ANAMNESIS_REPORT', { anamnesisId: id }),
};

export const perinealAssessmentsApi = {
  list: (patientId: string) =>
    api.get<PerinealAssessment[]>(`/perineal-assessments?patientId=${patientId}`),
  getById: (id: string) => api.get<PerinealAssessment>(`/perineal-assessments/${id}`),
  create: (data: { patientId: string; data: Record<string, unknown> }) =>
    api.post<PerinealAssessment>('/perineal-assessments', data),
  update: (id: string, data: { data: Record<string, unknown> }) =>
    api.patch<PerinealAssessment>(`/perineal-assessments/${id}`, data),
  remove: (id: string) => api.delete<void>(`/perineal-assessments/${id}`),
};

export const evolutionsApi = {
  summaryReport: (patientId: string): Promise<Blob> =>
    documentsApi.generate('EVOLUTION_SUMMARY', { patientId }),
  list: (patientId: string) => api.get<Evolution[]>(`/evolutions?patientId=${patientId}`),
  getById: (id: string) => api.get<Evolution>(`/evolutions/${id}`),
  create: (data: {
    patientId: string;
    appointmentId: string;
    techniqueIds: string[];
    description?: string;
    evolutionDate?: string;
  }) => api.post<Evolution>('/evolutions', data),
  update: (
    id: string,
    data: { description?: string; evolutionDate?: string; appointmentId?: string | null; techniqueIds?: string[] },
  ) =>
    api.patch<Evolution>(`/evolutions/${id}`, data),
  remove: (id: string) => api.delete<void>(`/evolutions/${id}`),
};

export const treatmentPackagesApi = {
  list: (params?: { patientId?: string; status?: string }) =>
    api.get<TreatmentPackage[]>(`/treatment-packages?${queryString(params)}`),
  getById: (id: string) => api.get<TreatmentPackage>(`/treatment-packages/${id}`),
  create: (data: {
    name: string;
    patientId: string;
    procedureIds: string[];
    totalSessions: number;
    totalPrice: number;
    notes?: string;
    paymentMethod?: string;
    installments?: number;
    dueDate?: string;
    downPayment?: number;
    downPaymentDueDate?: string;
    customInstallments?: Array<{ amount: number; dueDate: string; paymentMethod?: string }>;
  }) => api.post<TreatmentPackage>('/treatment-packages', data),
  update: (id: string, data: { name?: string; notes?: string; status?: string }) =>
    api.patch<TreatmentPackage>(`/treatment-packages/${id}`, data),
  remove: (id: string) => api.delete<void>(`/treatment-packages/${id}`),
};

type RawFinancialRecord = Omit<FinancialRecord, 'installment'> & {
  installment?: number;
  installmentTotal?: number;
};

function normalizeFinancialRecord(raw: RawFinancialRecord): FinancialRecord {
  const { installment, installmentTotal, ...rest } = raw;
  return {
    ...rest,
    ...(installment != null && installmentTotal != null
      ? { installment: { current: installment, total: installmentTotal } }
      : {}),
  };
}

function normalizeFinancialRecords(raw: RawFinancialRecord[]): FinancialRecord[] {
  return raw.map(normalizeFinancialRecord);
}

export const financialApi = {
  list: async (params: ({ month: number; year: number } | { startDate: string; endDate: string }) & { page?: number; limit?: number }) => {
    const raw = await api.get<{ data: RawFinancialRecord[]; meta: PaginatedResponse<unknown>['meta'] }>(`/financial?${queryString(params)}`);
    return { data: normalizeFinancialRecords(raw.data), meta: raw.meta };
  },
  listByPatient: async (patientId: string) =>
    normalizeFinancialRecords(await api.get<RawFinancialRecord[]>(`/financial/patient/${patientId}`)),
  summary: (params: { month: number; year: number }) =>
    api.get<FinancialSummary>(`/financial/summary?${queryString(params)}`),
  getById: async (id: string) =>
    normalizeFinancialRecord(await api.get<RawFinancialRecord>(`/financial/${id}`)),
  create: async (data: { patientId?: string; amount: number; type: string; description?: string; paymentMethod?: string; appointmentId?: string; installments?: number; dueDate?: string; isRecurring?: boolean; recurrenceMonths?: number }) => {
    const result = await api.post<RawFinancialRecord | RawFinancialRecord[]>('/financial', data);
    return Array.isArray(result) ? normalizeFinancialRecords(result) : normalizeFinancialRecord(result);
  },
  update: async (id: string, data: Record<string, unknown>) =>
    normalizeFinancialRecord(await api.patch<RawFinancialRecord>(`/financial/${id}`, data)),
  remove: (id: string, mode: 'single' | 'this_and_future' = 'single') =>
    api.delete<void>(`/financial/${id}?mode=${mode}`),
};

export const organizationApi = {
  getPlanUsage: () => api.get<PlanUsage>('/organizations/me/plan'),
  getProfile: () => api.get<OrganizationProfile>('/organizations/me'),
  update: (data: UpdateOrganizationData) => api.patch<OrganizationProfile>('/organizations/me', data),
};

export interface ClinicDocument {
  id: string;
  organizationId: string | null;
  name: string;
  description: string | null;
  category: string | null;
  type: 'FILE' | 'GENERATED';
  fileKey: string | null;
  mimeType: string | null;
  fileSize: number | null;
  templateType: string | null;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableTemplate {
  template: string;
  name: string;
  description: string;
  requiredContext: string[];
}

async function requestBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/v1${path}`, {
      ...options,
      credentials: 'include',
      signal: controller.signal,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const message = Array.isArray(body.message)
        ? body.message.join(' ')
        : body.message || 'Erro na requisição';
      throw new ApiError(res.status, message, Array.isArray(body.errors) ? body.errors : undefined);
    }
    return res.blob();
  } finally {
    clearTimeout(timeoutId);
  }
}

export function openPdfBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const documentsApi = {
  list: () => api.get<ClinicDocument[]>('/documents'),
  listTemplates: () => api.get<AvailableTemplate[]>('/documents/templates'),
  upload: (formData: FormData) =>
    fetchWithRefresh(`${API_BASE_URL}/api/v1/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, body.message || 'Erro no upload');
      }
      return res.json() as Promise<ClinicDocument>;
    }),
  download: (id: string) =>
    requestBlob(`/documents/${id}/download`),
  generate: (template: string, context: Record<string, string>) =>
    requestBlob(`/documents/generate/${template}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ context }),
    }),
  update: (id: string, data: Partial<Pick<ClinicDocument, 'name' | 'description' | 'category' | 'active'>>) =>
    api.patch<ClinicDocument>(`/documents/${id}`, data),
  remove: (id: string) => api.delete<void>(`/documents/${id}`),
};

export const physicalExamsApi = {
  list: (patientId: string) =>
    api.get<PhysicalExam[]>(`/physical-exams?${queryString({ patientId })}`),
  getById: (id: string) => api.get<PhysicalExam>(`/physical-exams/${id}`),
  create: (data: { patientId: string; appointmentId?: string; examDate?: string }) =>
    api.post<PhysicalExam>('/physical-exams', data),
  update: (
    id: string,
    data: {
      baseVersion: number;
      sections?: PhysicalExamSections;
      examDate?: string;
      status?: PhysicalExamStatus;
    },
  ) => api.patch<PhysicalExam>(`/physical-exams/${id}`, data),
  remove: (id: string) => api.delete<void>(`/physical-exams/${id}`),
  report: (id: string): Promise<Blob> =>
    documentsApi.generate('PHYSICAL_EXAM_REPORT', { physicalExamId: id }),
};

export const tasksApi = {
  list: (params?: { status?: string; priority?: TaskPriority; assignedToId?: string }) =>
    api.get<Task[]>(`/tasks?${queryString(params)}`),
  my: (status?: string) =>
    api.get<Task[]>(`/tasks/my${status ? `?status=${status}` : ''}`),
  create: (data: {
    title: string;
    description?: string;
    priority?: TaskPriority;
    dueDate?: string | null;
    assignedToId: string;
  }) => api.post<Task>('/tasks', data),
  update: (
    id: string,
    data: {
      title?: string;
      description?: string | null;
      priority?: TaskPriority;
      dueDate?: string | null;
      assignedToId?: string;
      status?: TaskStatus;
    },
  ) => api.patch<Task>(`/tasks/${id}`, data),
  remove: (id: string) => api.delete<void>(`/tasks/${id}`),
};

export const subscriptionApi = {
  getStatus: () => api.get<PlanFeatureStatus>('/subscription/status'),
  getCurrent: () => api.get<SubscriptionData>('/subscription'),
  getPlans: () => api.get<Plan[]>('/subscription/plans'),
  changePlan: (planId: string) => api.patch<{ ok: boolean; planId: string; planName: string }>('/subscription/plan', { planId }),
  cancel: () => api.post<{ ok: boolean; endDate: string }>('/subscription/cancel', {}),
};

export const patientPortalApi = {
  invite: (patientId: string) =>
    api.post<{ message: string }>(`/patient-portal/patients/${patientId}/invite`, {}),

  resendConsent: (linkId: string) =>
    api.post<{ message: string }>(`/patient-portal/links/${linkId}/resend`, {}),

  getPortalStatus: (patientId: string) =>
    api.get<PatientPortalInfo>(`/patient-portal/patients/${patientId}/portal`),

  updatePlan: (patientId: string, features: PatientTreatmentPlanFeatures) =>
    api.put<PatientTreatmentPlanFeatures>(`/patient-portal/patients/${patientId}/plan`, { features }),

  listVoidingDiaryEntries: (patientId: string) =>
    api.get<VoidingDiaryEntry[]>(`/patient-portal/patients/${patientId}/voiding-diary/entries`),

  listBowelDiaryEntries: (patientId: string) =>
    api.get<BowelDiaryEntry[]>(`/patient-portal/patients/${patientId}/bowel-diary/entries`),

  activate: (token: string, password: string) =>
    api.post<{ message: string }>('/patient-portal/auth/activate', { token, password }),
};

export const supportApi = {
  createTicket: (data: {
    category: 'BUG' | 'SUGGESTION' | 'QUESTION';
    description: string;
    context: {
      route: string;
      url: string;
      userAgent: string;
      appVersion: string;
      occurredAt: string;
    };
    sentryEventId: string | null;
  }) => api.post<{ ok: true }>('/support/tickets', data),
};
